import React, { useState } from 'react'

/**
 * Shown when a sign-in lands on an account that is scheduled for deletion.
 * The server withheld the session, so nothing is signed in behind this — the
 * caller either restores the account here or stays signed out.
 *
 * `onConfirm` belongs to the caller because each route proves identity
 * differently: Google spent its handshake in the callback and holds a one-time
 * token, a password login re-sends the password, an email code re-sends the
 * code. Only the question is shared, not the answer.
 */
export default function ReactivateModal({ purgeAt, onConfirm, onRestored, onCancel }) {
	const [isWorking, setIsWorking] = useState(false)
	const [errorText, setErrorText] = useState('')

	const purgeDate = purgeAt
		? new Date(purgeAt).toLocaleDateString('en-US', {
				month: 'long',
				day: 'numeric',
				year: 'numeric',
			})
		: ''

	const restore = async () => {
		setErrorText('')
		setIsWorking(true)
		try {
			onRestored(await onConfirm())
		} catch (err) {
			setErrorText(
				err.response?.data?.error || 'Could not restore your account.'
			)
			setIsWorking(false)
		}
	}

	return (
		<dialog className="modal modal-open">
			<div className="modal-box searches-modal-box-custom">
				<h2 className="font-bold text-lg text-gray-200">
					Restore your account?
				</h2>
				<p className="pt-4 py-2 text-gray-200">
					This account is scheduled for deletion
					{purgeDate ? ` on ${purgeDate}` : ''}. Restore it to sign in — leave
					it and it&apos;s deleted that day.
				</p>

				{errorText && (
					<p className="text-red-500 text-sm py-2">{errorText}</p>
				)}

				<div className="modal-action">
					<button
						type="button"
						className="btn btn-ghost"
						disabled={isWorking}
						onClick={onCancel}
					>
						Not now
					</button>
					<button
						type="button"
						className="btn btn-primary"
						disabled={isWorking}
						onClick={restore}
					>
						{isWorking ? 'Restoring…' : 'Restore my account'}
					</button>
				</div>
			</div>
		</dialog>
	)
}
